import _ from 'lodash';
import * as Constants from "../utils/constants"
import { v4 as uuidv4 } from 'uuid';

export const useDynamicTableRows = () => {
    const createNewTable = (editorTableTemplate, editorFile, editorFileManager) => {
        const table = _.cloneDeep(editorTableTemplate);
        table.Id = `Table${uuidv4()}`;

        for (const row of table.Items) {
            row.Id = `TableRow${uuidv4()}`;

            for (const column of row.Items) {
                column.Id = `TableColumn${uuidv4()}`;

                const firstText = editorFileManager.findEditorElementByType(null, column, Constants.editorType.text);
                if (!firstText)
                    continue;

                const emptyText = editorFileManager.createEmptyEditorText(editorFile, firstText);
                emptyText.ParentTableId = table.Id;

                column.Items = [];
                column.Items.push(emptyText);
            }
        }

        return table;
    }

    const getBreakTableBody = (editorFile, editorText, editorFileManager) => {
        if (!editorText.ParentBreakTableBodyId || !editorText.ParentTableId)
            return;

        const editorBreakTableBody = editorFileManager.findEditorElementById(editorFile, null, editorText.ParentBreakTableBodyId);

        if (!editorBreakTableBody || !editorBreakTableBody.Items || editorBreakTableBody.Items.length === 0)
            return;

        return editorBreakTableBody;
    }

    /**
     * Called on rich textvalue change. Determines if a new dynamic row needs to be added   
     */
    const addDynamicTableRowIfNeeded = (editorFile, editorText, value, editorFileManager) => {
        if (!editorFile || !editorText || !editorText.ParentBreakTableBodyId || !editorText.ParentTableId || !value)
            return;

        const editorBreakTableBody = getBreakTableBody(editorFile, editorText, editorFileManager);
        if (!editorBreakTableBody)
            return;

        // DXML Table has only one row
        // In the body of breakTable, one table is one dynamic table row.
        const lastEditorTable = editorBreakTableBody.Items?.findLast(x => x.Type === Constants.editorType.table);
        if (!lastEditorTable || lastEditorTable.Id !== editorText.ParentTableId)
            return;

        if (editorFileManager.isAllEditorTableRowsFilled(lastEditorTable)) {
            const newEditorTable = createNewTable(lastEditorTable, editorFile, editorFileManager);
            editorBreakTableBody.Items.push(newEditorTable);
        }
    }

    const removeLastDynamicTableRowIfNeeded = (editorFile, editorText, value, editorFileManager) => {
        if (!editorFile || !editorText || !editorText.ParentBreakTableBodyId || !editorText.ParentTableId || value.trim().length > 0)
            return;

        const editorBreakTableBody = getBreakTableBody(editorFile, editorText, editorFileManager);
        if (!editorBreakTableBody || !editorBreakTableBody.Items)
            return;

        const editorTables = editorBreakTableBody.Items.filter(x => x.Type === Constants.editorType.table);
        if (!editorTables || editorTables.length < 2)
            return;

        // editing text in the second last table row
        const secondLastEditorTable = editorTables[editorBreakTableBody.Items.length - 2];
        if (secondLastEditorTable?.Id !== editorText.ParentTableId)
            return;

        // at least one empty column in the second last table row
        if (editorFileManager.isAllEditorTableRowsFilled(secondLastEditorTable))
            return;

        const lastEditorTable = editorTables.at(-1);
        if (editorFileManager.isEditorTableEmpty(lastEditorTable)) {
            const foundIndex = editorBreakTableBody.Items.findIndex(x => x.Id === lastEditorTable.Id);
            if (foundIndex < 0)
                return;
            editorBreakTableBody.Items.splice(foundIndex, 1);
        }
    }

    const insertRowAbove = (tableId, editorBreakTableBody, editorFile, editorFileManager) => {
        if (!tableId || !editorFile || !editorBreakTableBody || !editorBreakTableBody.Items)
            return;

        const index = editorBreakTableBody.Items.findIndex(x => x.Id === tableId);
        if (index < 0)
            return;

        const newEditorTable = createNewTable(editorBreakTableBody.Items[index], editorFile, editorFileManager);
        editorBreakTableBody.Items.splice(index, 0, newEditorTable);
    }

    const insertRowBelow = (tableId, editorBreakTableBody, editorFile, editorFileManager) => {
        if (!tableId || !editorFile || !editorBreakTableBody || !editorBreakTableBody.Items)
            return;

        const index = editorBreakTableBody.Items.findIndex(x => x.Id === tableId);
        if (index < 0)
            return;

        const newEditorTable = createNewTable(editorBreakTableBody.Items[index], editorFile, editorFileManager);
        editorBreakTableBody.Items.splice(index + 1, 0, newEditorTable);
    }

    const deleteRow = (tableId, editorBreakTableBody) => {
        if (!tableId || !editorBreakTableBody || !editorBreakTableBody.Items)
            return;

        const index = editorBreakTableBody.Items.findIndex(x => x.Id === tableId);
        if (index < 0)
            return;

        editorBreakTableBody.Items.splice(index, 1);
    }

    const moveUpRow = (tableId, editorBreakTableBody) => {
        if (!tableId || !editorBreakTableBody || !editorBreakTableBody.Items)
            return;

        const tableIdx = editorBreakTableBody.Items.findIndex(x => x.Id === tableId && x.Type === Constants.editorType.table);
        if (tableIdx <= 0)
            return;

        const prevTableIdx = editorBreakTableBody.Items.findLastIndex((x, index) => index < tableIdx && x.Type === Constants.editorType.table);
        if (prevTableIdx < 0)
            return;

        [editorBreakTableBody.Items[tableIdx], editorBreakTableBody.Items[prevTableIdx]] = [editorBreakTableBody.Items[prevTableIdx], editorBreakTableBody.Items[tableIdx]];
    }

    const moveDownRow = (tableId, editorBreakTableBody) => {
        if (!tableId || !editorBreakTableBody || !editorBreakTableBody.Items)
            return;

        const tableIdx = editorBreakTableBody.Items.findIndex(x => x.Id === tableId && x.Type === Constants.editorType.table);
        if (tableIdx < 0)
            return;

        const nextTableIdx = editorBreakTableBody.Items.findIndex((x, index) => index > tableIdx && x.Type === Constants.editorType.table);
        if (nextTableIdx < 0)
            return;

        [editorBreakTableBody.Items[tableIdx], editorBreakTableBody.Items[nextTableIdx]] = [editorBreakTableBody.Items[nextTableIdx], editorBreakTableBody.Items[tableIdx]];
    }

    return {
        addDynamicTableRowIfNeeded,
        removeLastDynamicTableRowIfNeeded,
        insertRowAbove,
        insertRowBelow,
        deleteRow,
        moveUpRow,
        moveDownRow,
    }
}